import React, { useState, useEffect, useLayoutEffect } from 'react';

// Components
import Breadcrumbs from '../../../components/Breadcrumbs';

// Utils
import { getAlert, parseCurrency } from '../../../../utils/SamuiUtils';
import ArImage from "../../../components/Modal/ArImage";
import { 
  useDataTableKeyboardNavigation, 
  getDataTableRowStyle, 
  getDataTableKeyboardInstructions 
} from '../../../../utils/DataTableKeyboardNavigation';


const STORAGE_KEY = 'debtor-list';

function loadFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const o = JSON.parse(raw);
      return {
        searchTerm: o.searchTerm ?? '',
        sortKey: o.sortKey ?? '',
        sortDirection: o.sortDirection ?? '',
      };
    }
  } catch (_) {}
  return { searchTerm: '', sortKey: '', sortDirection: '' };
}

function Main({ masterList, detailList, name, onPageInsert, onRowSelected, activeAfterSaveId, onClearActiveAfterSave, idField = 'AR_Id', listPage, listItemsPerPage, onListPageChange, onListItemsPerPageChange }) {

  const stored = loadFromStorage();
  const [dataMasterList, setDataMasterList] = useState([]);
  const [internalPage, setInternalPage] = useState(1);
  const [internalItemsPerPage, setInternalItemsPerPage] = useState(50);
  const currentPage = listPage !== undefined ? listPage : internalPage;
  const itemsPerPage = listItemsPerPage !== undefined ? listItemsPerPage : internalItemsPerPage;
  const setCurrentPage = (p) => (onListPageChange ? onListPageChange(p) : setInternalPage(p));
  const setItemsPerPage = (n) => (onListItemsPerPageChange ? onListItemsPerPageChange(n) : setInternalItemsPerPage(n));
  const [searchTerm, setSearchTerm] = useState(stored.searchTerm);
  const [filteredItems, setFilteredItems] = useState([]);
  const [showPictureModal, setShowPictureModal] = useState(false);
  const [selectedData, setSelectedData] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: stored.sortKey, direction: stored.sortDirection });

  // DataTable keyboard navigation state
  const [selectedRowIndex, setSelectedRowIndex] = useState(0);
  const rowRefs = React.useRef([]);
  const activeAfterSaveIdRef = React.useRef(activeAfterSaveId);
  const prevSearchTermRef = React.useRef(searchTerm);

  // บันทึก state ลง localStorage เมื่อมีการเปลี่ยนแปลง (merge กับ listPage/listItemsPerPage จาก parent)
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const o = raw ? JSON.parse(raw) : {};
      const next = { ...o, searchTerm, sortKey: sortConfig.key, sortDirection: sortConfig.direction };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch (_) {}
  }, [searchTerm, sortConfig.key, sortConfig.direction]);

  // sync ref ก่อน filterItems (useLayoutEffect รันก่อน useEffect)
  useLayoutEffect(() => {
    activeAfterSaveIdRef.current = activeAfterSaveId;
  }, [activeAfterSaveId]);

  useEffect(() => {
    initialize();
  }, [masterList, detailList]);

  useEffect(() => {
    if (dataMasterList && dataMasterList.length > 0) {
      filterItems();
    }
  }, [searchTerm, dataMasterList, activeAfterSaveId]);

  useLayoutEffect(() => {
    if (activeAfterSaveId == null || filteredItems.length === 0) return;
    const sortedItems = getSortedItems(filteredItems);
    const targetId = parseCurrency(activeAfterSaveId);
    const idx = sortedItems.findIndex(item => {
      const itemId = parseCurrency(item[idField] ?? item.Ar_Id ?? item.AR_Id);
      return itemId === targetId;
    });
    if (idx >= 0) {
      const page = Math.floor(idx / itemsPerPage) + 1;
      const rowInPage = idx % itemsPerPage;
      setCurrentPage(page);
      setSelectedRowIndex(rowInPage);
      const timer = setTimeout(() => {
        rowRefs.current[rowInPage]?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
        onClearActiveAfterSave?.();
      }, 150);
      return () => clearTimeout(timer);
    } else {
      onClearActiveAfterSave?.();
    }
  }, [activeAfterSaveId, filteredItems, idField, itemsPerPage, onClearActiveAfterSave, sortConfig]);


  const initialize = async () => {
    try {
      if (masterList && masterList.length > 0) {
        setDataMasterList(masterList);
      } else {
        setDataMasterList([]);
        setFilteredItems([]);
      }
    } catch (error) {
      getAlert('FAILED', error.message);
    }
  };

  const filterItems = () => {
    try {
      if (!dataMasterList || dataMasterList.length === 0) {
        setFilteredItems([]);
        return;
      }

      // sync จาก prop เสมอ ก่อนเช็ค (แก้ effect ordering โดยเฉพาะตอนมี search)
      activeAfterSaveIdRef.current = activeAfterSaveId;

      let filtered = [...dataMasterList];

      // Search filtering
      if (searchTerm.trim() !== '') {
        const lowercasedSearchTerm = searchTerm.toLowerCase();
        filtered = filtered.filter(item =>
          (item.AR_Code && item.AR_Code.toLowerCase().includes(lowercasedSearchTerm)) ||
          (item.AR_Name && item.AR_Name.toLowerCase().includes(lowercasedSearchTerm)) ||
          (item.ARType_Name && item.ARType_Name.toLowerCase().includes(lowercasedSearchTerm)) ||
          (item.AR_Tel1 && item.AR_Tel1.toLowerCase().includes(lowercasedSearchTerm)) ||
          (item.AR_RefSaleCode && item.AR_RefSaleCode.toLowerCase().includes(lowercasedSearchTerm))
        );
      }

      setFilteredItems(filtered);
      // reset เฉพาะเมื่อเปลี่ยนคำค้น - เมื่อ data refresh (แก้ไขกลับมา) ไม่ reset เพื่อรักษาหน้า + itemsPerPage
      const searchChanged = prevSearchTermRef.current !== searchTerm;
      prevSearchTermRef.current = searchTerm;
      if (searchChanged) {
        setCurrentPage(1);
        setSelectedRowIndex(0);
      }
    
    } catch (error) {
      console.error('Filter error:', error.message);
      setFilteredItems([]);
    }
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    setSelectedRowIndex(0);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedItems = (items) => {
    if (!sortConfig.key) return items;
    
    return [...items].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];
      
      // Special handling for AR_Image column
      if (sortConfig.key === 'AR_Image') {
        aValue = (a.AR_Image && a.AR_Image.length > 0) ? 1 : 0; // มีรูป = 1, ไม่มีรูป = 0
        bValue = (b.AR_Image && b.AR_Image.length > 0) ? 1 : 0;
      }
      
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const result = aValue.toLowerCase().localeCompare(bValue.toLowerCase());
        return sortConfig.direction === 'asc' ? result : -result;
      }
      
      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const renderPageNumbers = () => {
    const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
    const pages = [];
    const maxVisiblePages = 5;
    const range = 2;

    if (totalPages > maxVisiblePages) {

      pages.push(
        <li
          key={1}
          className={`paginate_button page-item ${currentPage === 1 ? "active" : ""}`}
        >
          <a href="#" className="page-link" onClick={(e) => { e.preventDefault(); handlePageChange(1); }}>
            1
          </a>
        </li>
      );

      if (currentPage - range > 2) {
        pages.push(
          <li key="ellipsis-start" className="paginate_button page-item">
            <a href="#" className="page-link">...</a>
          </li>
        );
      }

      for (let i = Math.max(currentPage - range, 2); i <= Math.min(currentPage + range, totalPages - 1); i++) {
        pages.push(
          <li
            key={i}
            className={`paginate_button page-item ${currentPage === i ? "active" : ""}`}
          >
            <a href="#" className="page-link" onClick={(e) => { e.preventDefault(); handlePageChange(i); }}>
              {i}
            </a>
          </li>
        );
      }

      if (currentPage + range < totalPages - 1) {
        pages.push(
          <li key="ellipsis-end" className="paginate_button page-item">
            <a href="#" className="page-link">...</a>
          </li>
        );
      }

      pages.push(
        <li
          key={totalPages}
          className={`paginate_button page-item ${currentPage === totalPages ? "active" : ""}`}
        >
          <a href="#" className="page-link" onClick={(e) => { e.preventDefault(); handlePageChange(totalPages); }}>
            {totalPages}
          </a>
        </li>
      );
    } else {

      for (let i = 1; i <= totalPages; i++) {
        pages.push(
          <li
            key={i}
            className={`paginate_button page-item ${currentPage === i ? "active" : ""}`}
          >
            <a href="#" className="page-link" onClick={(e) => { e.preventDefault(); handlePageChange(i); }}>
              {i}
            </a>
          </li>
        );
      }
    }

    return pages;
  };

  const handleItemsPerPageChange = (event) => {
    setItemsPerPage(parseCurrency(event.target.value));
    setCurrentPage(1);
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const sortedItems = getSortedItems(filteredItems);
  const currentItems = sortedItems.slice(indexOfFirstItem, indexOfLastItem);

  // Field mappings for keyboard navigation
  const fieldMappings = {
    no: 'AR_Id'
  };

  // Keyboard navigation
  useDataTableKeyboardNavigation(
    true, // isActive
    currentItems,
    selectedRowIndex,
    setSelectedRowIndex,
    onRowSelected,
    fieldMappings,
    currentPage,
    handlePageChange,
    filteredItems.length,
    itemsPerPage
  );

  return (
    <>
      <div className="page-header d-flex justify-content-between align-items-center mb-0">
        <Breadcrumbs page={name} items={[
          { name: 'ข้อมูลตั้งต้น', url: '/initialdata' },
          { name: name, url: '/debtor' },
        ]} />
      </div>
      <div className='p-3 rounded-3 my-2 shadow-lg' style={{ backgroundColor: '#ffff' }}>
        <div className='row'>
          <div className="col-3 d-flex">
            <div className="navbar-form nav-search">
              <div className="input-group">
                <input
                  type="text"
                  placeholder="ค้นหาเอกสาร..."
                  className="form-control"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <span className="input-group-text">
                  <i className="fas fa-search"></i>
                </span>
              </div>
            </div>
          </div>
          <div className="col-9 d-flex justify-content-end">
            <button
              onClick={onPageInsert}
              className="btn btn-warning text-white ms-2"
              type="button"
            >
              สร้าง{name} <i className="fa fa-plus"></i>
            </button>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-12">
          <div className="card p-2">
            <div className="table-responsive" style={{ maxHeight: '600px', overflowY: 'auto' }}>
              <table id="basic-datatables" className="table table-striped table-hover">
                <thead className="thead-dark">
                  <tr>
                    {/* ข้อมูลลูกหนี้ */}
                    <th className="text-center" style={{ width: "2%" }}>
                      #
                    </th>
                    <th 
                      className="text-center" 
                      style={{ width: "10%", cursor: "pointer" }}
                      onClick={() => handleSort('AR_Code')}
                    >
                      รหัสลูกหนี้ 
                      {sortConfig.key === 'AR_Code' && (
                        <i className={`fas fa-sort-${sortConfig.direction === 'asc' ? 'up' : 'down'} ms-1`}></i>
                      )}
                    </th>
                    <th 
                      className="text-center" 
                      style={{ width: "30%", cursor: "pointer" }}
                      onClick={() => handleSort('AR_Name')}
                    >
                      ชื่อลูกหนี้
                      {sortConfig.key === 'AR_Name' && (
                        <i className={`fas fa-sort-${sortConfig.direction === 'asc' ? 'up' : 'down'} ms-1`}></i>
                      )}
                    </th>
                    <th 
                      className="text-center" 
                      style={{ width: "9%", cursor: "pointer" }}
                      onClick={() => handleSort('ARType_Name')}
                    >
                      ประเภทลูกหนี้
                      {sortConfig.key === 'ARType_Name' && (
                        <i className={`fas fa-sort-${sortConfig.direction === 'asc' ? 'up' : 'down'} ms-1`}></i>
                      )}
                    </th>
                    <th 
                      className="text-center" 
                      style={{ width: "10%", cursor: "pointer" }}
                      onClick={() => handleSort('AR_Tel1')}
                    >
                      เบอร์โทร
                      {sortConfig.key === 'AR_Tel1' && (
                        <i className={`fas fa-sort-${sortConfig.direction === 'asc' ? 'up' : 'down'} ms-1`}></i>
                      )}
                    </th>
                    <th 
                      className="text-center" 
                      style={{ width: "10%", cursor: "pointer" }}
                      onClick={() => handleSort('AR_RefSaleCode')}
                    >
                      ชื่ออ้างอิงการขาย
                      {sortConfig.key === 'AR_RefSaleCode' && (
                        <i className={`fas fa-sort-${sortConfig.direction === 'asc' ? 'up' : 'down'} ms-1`}></i>
                      )}
                    </th>
                    <th 
                      className="text-center" 
                      style={{ width: "10%", cursor: "pointer" }}
                      onClick={() => handleSort('AR_Credit_Limit')}
                    >
                      ยอดเครดิตลูกค้า
                      {sortConfig.key === 'AR_Credit_Limit' && (
                        <i className={`fas fa-sort-${sortConfig.direction === 'asc' ? 'up' : 'down'} ms-1`}></i>
                      )}
                    </th>
                    <th 
                      className="text-center" 
                      style={{ width: "11%", cursor: "pointer" }}
                      onClick={() => handleSort('AR_Credit_Term')}
                    >
                      ระยะเวลาเครดิตลูกค้า
                      {sortConfig.key === 'AR_Credit_Term' && (
                        <i className={`fas fa-sort-${sortConfig.direction === 'asc' ? 'up' : 'down'} ms-1`}></i>
                      )}
                    </th>
                    <th 
                      className="text-center" 
                      style={{ width: "10%", cursor: "pointer" }}
                      onClick={() => handleSort('AR_Image')}
                    >
                      รูปหน้าร้าน
                      {sortConfig.key === 'AR_Image' && (
                        <i className={`fas fa-sort-${sortConfig.direction === 'asc' ? 'up' : 'down'} ms-1`}></i>
                      )}
                    </th>
                    <th 
                      className="text-center" 
                      style={{ width: "5%", cursor: "pointer" }}
                      onClick={() => handleSort('AR_Status')}
                    >
                      สถานะ
                      {sortConfig.key === 'AR_Status' && (
                        <i className={`fas fa-sort-${sortConfig.direction === 'asc' ? 'up' : 'down'} ms-1`}></i>
                      )}
                    </th>
                    <th className="text-center" style={{ width: "3%" }}>
                      แก้ไข
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.length > 0 ? (
                    currentItems.map((item, index) => (
                      <tr
                        key={index}
                        ref={el => rowRefs.current[index] = el}
                        onClick={() => onRowSelected(item)}
                        style={{
                          ...getDataTableRowStyle(
                            index, 
                            selectedRowIndex, 
                            null, 
                            item, 
                            'AR_Id'
                          ),
                          cursor: "pointer"
                        }}
                      >
                        <td className="text-center">{indexOfFirstItem + index + 1}</td>
                        <td className="text-center">
                          {item.AR_Code}
                        </td>
                        <td className="text-left">{item.AR_Name}</td>
                        <td className="text-center">
                          {item.ARType_Name}
                        </td>
                        <td className="text-center">
                          {item.AR_Tel1}
                        </td>
                        <td className="text-center">
                          {item.AR_RefSaleCode}
                        </td>
                        <td className="text-center">
                          {item.AR_Credit_Limit}
                        </td>
                        <td className="text-center">
                          {item.AR_Credit_Term}
                        </td>
                        <td className="text-center">
                          {item.AR_Image && item.AR_Image.length > 0 ? (
                            <i
                              className="fas fa-image text-primary fw-bold"
                              title="มีรูปภาพ"
                              onClick={() => {
                                setSelectedData(item);
                                setShowPictureModal(true);
                              }}
                              style={{ cursor: "pointer" }}
                            />
                          ) : (
                            <div className="">
                              - 
                            </div>
                          )}
                        </td>
                        <td className="text-center">
                          {item.AR_Status === "Y"
                            ? "ใช้งาน"
                            : "ไม่ใช้งาน"}
                        </td>
                        <td className="text-center">
                          <i onClick={() => onRowSelected(item)} className="fas fa-edit fw-bold" style={{ color: "#EF6C00" }} />
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="11">
                        <center>
                          <h5>ไม่พบข้อมูล</h5>
                        </center>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            <div className="row mt-3">
              <div className="col-12 d-flex justify-content-between">
                {/* Dropdown for Items Per Page */}
                <div className="d-flex align-items-center">
                  <label htmlFor="itemsPerPage" className="mr-2">Items per page:</label>
                  <select
                    id="itemsPerPage"
                    value={itemsPerPage}
                    onChange={handleItemsPerPageChange}
                    className="form-select"
                  >
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={150}>150</option>
                    <option value={filteredItems.length}>All</option>
                  </select>
                </div>

                <div className="dataTables_paginate paging_simple_numbers">
                  <ul className="pagination">
                    <li className={`paginate_button page-item previous ${currentPage === 1 ? "disabled" : ""}`}>
                      <a
                        href="#"
                        className="page-link"
                        onClick={(e) => { e.preventDefault(); handlePageChange(currentPage - 1); }}
                      >
                        Previous
                      </a>
                    </li>
                    {renderPageNumbers()}
                    <li
                      className={`paginate_button page-item next ${currentPage ===
                        Math.ceil(filteredItems.length / itemsPerPage)
                        ? "disabled"
                        : ""
                        }`}
                    >
                      <a
                        href="#"
                        className="page-link"
                        onClick={(e) => { e.preventDefault(); handlePageChange(currentPage + 1); }}
                      >
                        Next
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="col-12">
                <div className="text-muted mt-2" style={{ fontSize: "12px" }}>
                  {getDataTableKeyboardInstructions(false, false)}
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
       {showPictureModal && (
        <ArImage
          onClose={() => setShowPictureModal(false)}
          isOpen={showPictureModal}
          arId={selectedData ? selectedData.AR_Id : null}
        />
      )}
    </>
  );
}

export default Main;